/**
 * V66 — useOrgLeaveCalendar
 *
 * 조직 휴가 캘린더 데이터 조회 훅.
 * BE callable `getOrgLeaveCalendar` 호출 + 모듈 레벨 in-memory 캐시(5분 TTL).
 *
 * - 입력: { year, month } (month: 1~12)
 * - SSoT: BE — leaveRequests.status==='approved' && approvalRequests.status==='approved'
 * - FE 가공: items 그대로 사용. 다일 휴가 expand 는 페이지 컴포넌트가 담당.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

export type OrgLeaveCalendarUnit =
  | 'fullDay'
  | 'halfDayMorning'
  | 'halfDayAfternoon'
  | 'hourly'
  | string;

export interface OrgLeaveCalendarItem {
  leaveRequestId: string;
  approvalRequestId: string | null;
  employeeId: string;
  employeeName: string;
  orgUnitId: string;
  orgUnitName: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  unit: OrgLeaveCalendarUnit;
  startDate: string; // YYYY-MM-DD (V66: 외부지원이면 프로젝트 기간으로 clip 된 시작일)
  endDate: string;   // (V66: 외부지원이면 프로젝트 기간으로 clip 된 종료일)
  startTime?: string; // 'HH:mm'
  endTime?: string;
  totalDays: number;
  /** V66: 타팀지원(외부 조직) 인원 여부 */
  isExternalSupport?: boolean;
  /** V66: 타팀지원이면 매칭된 프로젝트명 (tooltip 보강용) */
  supportProjectName?: string;
  /** V66: 휴직(LoA) discriminator. 'leaveOfAbsence' 면 loaType / loaTypeName 사용 */
  kind?: 'leave' | 'leaveOfAbsence';
  loaType?: 'parental' | 'familyCare' | 'general' | 'medical';
  loaTypeName?: string;
}

interface GetOrgLeaveCalendarResponse {
  success: boolean;
  data: {
    items: OrgLeaveCalendarItem[];
    totalCount: number;
    scopeMode: 'SELF' | 'SUBTREE' | 'ALL';
    scopeOrgUnitIds: string[];
    year: number;
    month: number;
    monthStart: string;
    monthEnd: string;
  };
  message?: string;
}

interface CacheEntry {
  items: OrgLeaveCalendarItem[];
  scopeMode: 'SELF' | 'SUBTREE' | 'ALL';
  totalCount: number;
  monthStart: string;
  monthEnd: string;
  timestamp: number;
}

const TTL_MS = 5 * 60 * 1000; // 5분
const cache = new Map<string, CacheEntry>();

function cacheKey(year: number, month: number): string {
  return `${year}-${month}`;
}

export interface UseOrgLeaveCalendarResult {
  items: OrgLeaveCalendarItem[];
  scopeMode: 'SELF' | 'SUBTREE' | 'ALL' | null;
  totalCount: number;
  monthStart: string | null;
  monthEnd: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * 같은 월 캐시 무효화 (휴가 승인/취소 직후 호출 가능)
 */
export function invalidateOrgLeaveCalendarCache(year?: number, month?: number): void {
  if (year !== undefined && month !== undefined) {
    cache.delete(cacheKey(year, month));
  } else {
    cache.clear();
  }
}

export function useOrgLeaveCalendar(year: number, month: number): UseOrgLeaveCalendarResult {
  const [items, setItems] = useState<OrgLeaveCalendarItem[]>([]);
  const [scopeMode, setScopeMode] = useState<'SELF' | 'SUBTREE' | 'ALL' | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [monthStart, setMonthStart] = useState<string | null>(null);
  const [monthEnd, setMonthEnd] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetching = useRef(false);

  const fetch = useCallback(
    async (force = false) => {
      if (fetching.current) return;
      const key = cacheKey(year, month);
      const cached = cache.get(key);
      if (!force && cached && Date.now() - cached.timestamp < TTL_MS) {
        setItems(cached.items);
        setScopeMode(cached.scopeMode);
        setTotalCount(cached.totalCount);
        setMonthStart(cached.monthStart);
        setMonthEnd(cached.monthEnd);
        setIsLoading(false);
        setError(null);
        return;
      }

      if (!functions) {
        setItems([]);
        setIsLoading(false);
        return;
      }

      try {
        fetching.current = true;
        setIsLoading(true);
        setError(null);

        const fn = httpsCallable<
          { year: number; month: number },
          GetOrgLeaveCalendarResponse
        >(functions, 'getOrgLeaveCalendar');
        const result = await fn({ year, month });

        if (result.data?.success && result.data.data) {
          const d = result.data.data;
          cache.set(key, {
            items: d.items,
            scopeMode: d.scopeMode,
            totalCount: d.totalCount,
            monthStart: d.monthStart,
            monthEnd: d.monthEnd,
            timestamp: Date.now(),
          });
          setItems(d.items);
          setScopeMode(d.scopeMode);
          setTotalCount(d.totalCount);
          setMonthStart(d.monthStart);
          setMonthEnd(d.monthEnd);
        } else {
          throw new Error(result.data?.message || '조직 휴가 캘린더 조회 실패');
        }
      } catch (err: any) {
        console.error('[useOrgLeaveCalendar] failed:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setItems([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
        fetching.current = false;
      }
    },
    [year, month]
  );

  useEffect(() => {
    fetch(false);
  }, [fetch]);

  const refetch = useCallback(() => {
    invalidateOrgLeaveCalendarCache(year, month);
    fetch(true);
  }, [year, month, fetch]);

  return {
    items,
    scopeMode,
    totalCount,
    monthStart,
    monthEnd,
    isLoading,
    error,
    refetch,
  };
}
